import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

const DragAndDropTarget = ({ 
  onFileUpload, 
  message, 
  maxSizeMB = 50, 
  multiple = false, 
  // Updated default to allow images
  accept = { 
    'image/jpeg': ['.jpg', '.jpeg'], 
    'image/gif': ['.gif'] 
  } 
}) => {
  const maxSizeInBytes = maxSizeMB * 1024 * 1024;

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      onFileUpload(acceptedFiles);
    }
  };

  const onDropRejected = (fileRejections) => {
    const messages = new Set();

    fileRejections.forEach((rejection) => {
      rejection.errors.forEach((err) => {
        if (err.code === 'file-too-large') {
          messages.add(`- One or more files exceed the ${maxSizeMB}MB limit.`);
        } else if (err.code === 'file-invalid-type') {
          // Extracts extensions like .jpg, .gif for the error message
          const allowedExtensions = Object.values(accept).flat().join(', ');
          messages.add(`- Invalid file type. Allowed: ${allowedExtensions}`);
        } else if (err.code === 'too-many-files') {
          messages.add(`- Only one file can be uploaded at a time.`);
        } else {
          messages.add(`- ${err.message}`);
        }
      });
    });

    if (messages.size > 0) {
      alert(`Please fix the following issues:\n${Array.from(messages).join('\n')}`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: maxSizeInBytes,
    accept,
    multiple
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : '#bdbdbd',
        backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
        padding: '2.5rem',
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '12px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'rgba(25, 118, 210, 0.08)'
        }
      }}
    >
      <input {...getInputProps()} />
      <Typography variant="h6" color="textPrimary" gutterBottom>
        {isDragActive ? "Drop them now!" : message}
      </Typography>
      
      <Typography variant="body2" color="textSecondary">
        Accepted: {Object.values(accept).flat().join(', ')}
      </Typography>
      
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        Max size: {maxSizeMB}MB | {multiple ? "Multiple files" : "Single file"}
      </Typography>
    </Box>
  );
};

DragAndDropTarget.propTypes = {
  onFileUpload: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  maxSizeMB: PropTypes.number,
  multiple: PropTypes.bool,
  accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
};

export default DragAndDropTarget;